import { resolveFieldKindFromChecks } from './form-io-traverse.util';
import { SUBMITTED_AT_DATE_FILTER_FORMAT, SUBMITTED_AT_RAW_ROW_KEY } from './form-submission-constants.util';
import {
  buildSignatureViewFileHtml,
  formatCurrencyCell,
  formatFormSubmissionDatetimeCell,
  formatSelectboxCell,
  formatSubmissionDataCell,
  formatSurveyCell,
} from './form-submission-cell-format.util';
import {
  formatFormSubmissionCreatedAtForDisplay,
  submissionCreatedAtToMs,
  submittedAtDateRangeOnCustomFilter,
} from './form-submission-date.util';
import { getFormSubmissionRowActions } from './form-submission-row-actions.util';
import { collectSortedDataKeysFromSubmissions } from './form-submission-schema.util';

const baseColumn = (name, displayName, cellType = 'value') => ({
  name,
  displayName,
  sortOrder: 'none',
  isFilterable: true,
  isSortable: true,
  isVisible: true,
  type: cellType,
});

const buildSubmittedAtMetadataColumn = () => ({
  ...baseColumn('submitted_at', 'Submitted'),
  filter: {
    type: 'date-range',
    dateFormat: SUBMITTED_AT_DATE_FILTER_FORMAT,
    monthsShown: 2,
    onCustomFilterCallback: submittedAtDateRangeOnCustomFilter,
  },
});

/** DataGrid preset `LearnerCell` (same pattern as Feeds Audit user column). */
const buildUserMetadataColumn = () => ({
  name: 'user',
  displayName: 'User',
  sortOrder: 'none',
  isFilterable: true,
  isSortable: true,
  isVisible: true,
  type: 'preDefined',
  preset: 'LearnerCell',
  sortByProp: 'username',
  filter: {
    type: 'LearnerCell',
  },
});

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

/**
 * Facility + housing-unit ordering, matching {@link getBaseAuditColumns} / Audit.service.js `getLocationOptions`.
 * @param {readonly { locationId?: number, locationName?: string, locationTypeId?: number, parentLocationId?: number }[]} locations
 * @returns {string[]}
 */
export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

/**
 * @param {unknown} value
 * @returns {string}
 */
const nonEmptyString = (value) =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : '';

/**
 * MultiTagCell filter (frontend-commons) matches `preDefineComponentProps.location_name.tags[].label`.
 * @param {string} locationName
 * @returns {{ tags: { label: string }[] }}
 */
const locationTagsForMultiTagFilter = (locationName) => {
  const s = nonEmptyString(locationName);
  if (s === '') {
    return { tags: [] };
  }
  return { tags: [{ label: s }] };
};

/** Same shape as Audit hidden `location` column: multi-select via MultiTagCheckboxFilter. */
const buildLocationMetadataColumn = (locationOptions) => ({
  ...baseColumn('location_name', 'Location'),
  filter: {
    type: 'MultiTagCell',
    showBlankFilter: true,
  },
  options: Array.isArray(locationOptions) ? locationOptions : [],
});

/**
 * Props for DataGrid `LearnerCell` from API `person` on a submission row.
 * @param {{ username?: string | null, firstName?: string | null, lastName?: string | null, docId?: string | null } | null | undefined} person
 */
const userCellPropsFromPerson = (person) => ({
  username: nonEmptyString(person?.username),
  firstName: nonEmptyString(person?.firstName),
  lastName: nonEmptyString(person?.lastName),
  docId: nonEmptyString(person?.docId),
});

/**
 * @param {{ id?: string, created_at?: string, reference_id?: string | null, data?: unknown, location_name?: string | null } | null | undefined} submission
 * @param {readonly string[]} dataKeys
 * @param {ReadonlySet<string>} signatureKeys
 * @param {ReadonlyMap<string, string>} currencyMeta
 * @param {ReadonlySet<string>} selectboxKeys
 * @param {ReadonlySet<string>} surveyKeys
 * @param {ReadonlySet<string>} datetimeKeys
 * @returns {Record<string, string>}
 */
const GRID_FIELD_KIND_CHECKS = [
  ['signature', (key, ctx) => ctx.signatureKeys.has(key)],
  ['currency', (key, ctx) => ctx.currencyMeta.has(key)],
  ['selectbox', (key, ctx) => ctx.selectboxKeys.has(key)],
  ['survey', (key, ctx) => ctx.surveyKeys.has(key)],
  ['datetime', (key, ctx) => ctx.datetimeKeys.has(key)],
];

const resolveGridFieldKind = (key, ctx) =>
  resolveFieldKindFromChecks(GRID_FIELD_KIND_CHECKS, key, ctx);

const GRID_CELL_FORMATTERS = {
  signature: (cell) => buildSignatureViewFileHtml(cell),
  currency: (cell, key, ctx) => formatCurrencyCell(cell, ctx.currencyMeta.get(key)),
  selectbox: (cell) => formatSelectboxCell(cell),
  survey: (cell) => formatSurveyCell(cell),
  datetime: (cell) => formatFormSubmissionDatetimeCell(cell),
  default: (cell) => formatSubmissionDataCell(cell),
};

const formatCellByKind = (key, cell, ctx) => {
  const kind = resolveGridFieldKind(key, ctx);
  return GRID_CELL_FORMATTERS[kind](cell, key, ctx);
};

const getSubmissionDataRecord = (submission) => {
  const raw = submission?.data;
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
};

const buildSubmittedAtRowFields = (submission, createdAtMs) => {
  const hasCreatedAt = createdAtMs !== null;
  return {
    submitted_at: hasCreatedAt ? formatFormSubmissionCreatedAtForDisplay(submission?.created_at) : '',
    [SUBMITTED_AT_RAW_ROW_KEY]: hasCreatedAt ? new Date(createdAtMs).toISOString() : '',
  };
};

const buildSubmissionRowData = (
  submission,
  dataKeys,
  signatureKeys,
  currencyMeta,
  selectboxKeys,
  surveyKeys,
  datetimeKeys,
) => {
  const data = getSubmissionDataRecord(submission);
  const createdAtMs = submissionCreatedAtToMs(submission?.created_at);
  const rowData = {
    ...buildSubmittedAtRowFields(submission, createdAtMs),
    location_name: nonEmptyString(submission?.location_name),
  };
  const cellCtx = { signatureKeys, currencyMeta, selectboxKeys, surveyKeys, datetimeKeys };
  dataKeys.forEach((key) => {
    rowData[key] = formatCellByKind(key, data[key], cellCtx);
  });
  return rowData;
};

/**
 * @param {readonly { id?: string, created_at?: string, reference_id?: string | null, data?: unknown }[]} submissions
 * @param {ReadonlySet<string>} signatureKeys
 * @param {readonly string[]} locationFilterOptions
 */
export const buildFormSubmissionsColumns = (submissions, signatureKeys, locationFilterOptions = []) => {
  const dataKeys = collectSortedDataKeysFromSubmissions(submissions);
  const meta = [
    buildUserMetadataColumn(),
    buildLocationMetadataColumn(locationFilterOptions),
    buildSubmittedAtMetadataColumn(),
  ];
  const dynamic = dataKeys.map((key) =>
    baseColumn(key, key, signatureKeys.has(key) ? 'html' : 'value'),
  );
  return [...meta, ...dynamic];
};

const EMPTY_CURRENCY_META = /** @type {ReadonlyMap<string, string>} */ (new Map());
const EMPTY_SELECTBOX_KEYS = /** @type {ReadonlySet<string>} */ (new Set());
const EMPTY_SURVEY_KEYS = /** @type {ReadonlySet<string>} */ (new Set());
const EMPTY_DATETIME_KEYS = /** @type {ReadonlySet<string>} */ (new Set());

/**
 * @param {readonly { id?: string, created_at?: string, reference_id?: string | null, data?: unknown, location_name?: string | null, person?: { username?: string | null, firstName?: string | null, lastName?: string | null, docId?: string | null } }[]} submissions
 * @param {{
 *   dataKeys: readonly string[],
 *   signatureKeys: ReadonlySet<string>,
 *   currencyMeta?: ReadonlyMap<string, string>,
 *   selectboxKeys?: ReadonlySet<string>,
 *   surveyKeys?: ReadonlySet<string>,
 *   datetimeKeys?: ReadonlySet<string>,
 *   submissionActionHandlers?: object,
 * }} opts
 */
export const mapFormSubmissionsToGridData = (submissions, opts) => {
  const {
    dataKeys,
    signatureKeys,
    currencyMeta = EMPTY_CURRENCY_META,
    selectboxKeys = EMPTY_SELECTBOX_KEYS,
    surveyKeys = EMPTY_SURVEY_KEYS,
    datetimeKeys = EMPTY_DATETIME_KEYS,
    submissionActionHandlers,
  } = opts;
  if (!Array.isArray(submissions)) {
    return [];
  }
  return submissions.map((submission) => {
    const rowActions = getFormSubmissionRowActions(submission, submissionActionHandlers);
    return {
      isSelected: false,
      rowData: buildSubmissionRowData(
        submission,
        dataKeys,
        signatureKeys,
        currencyMeta,
        selectboxKeys,
        surveyKeys,
        datetimeKeys,
      ),
      preDefineComponentProps: {
        user: userCellPropsFromPerson(submission?.person),
        location_name: locationTagsForMultiTagFilter(submission?.location_name),
        ...(rowActions.length > 0 ? { actions: rowActions } : {}),
      },
    };
  });
};

const NAMED_COLUMN_STYLES = {
  submitted_at: { width: 200, minWidth: 200 },
  user: {
    width: 360,
    minWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  location_name: {
    minWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const HTML_COLUMN_STYLE = {
  width: 200,
  minWidth: 200,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const DEFAULT_COLUMN_STYLE = {
  width: 200,
  minWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
};

const resolveColumnStyle = (col) => {
  if (NAMED_COLUMN_STYLES[col.name]) {
    return NAMED_COLUMN_STYLES[col.name];
  }
  return col.type === 'html' ? HTML_COLUMN_STYLE : DEFAULT_COLUMN_STYLE;
};

/**
 * Per-column cell styles for long JSON / ids.
 * @param {readonly { name: string, type?: string }[]} columns
 */
export const buildFormSubmissionsTdStyles = (columns) =>
  columns.reduce((styles, col) => {
    styles[col.name] = resolveColumnStyle(col);
    return styles;
  }, /** @type {Record<string, Record<string, unknown>>} */ ({}));

/** Mirrors frontend-commons DataGrid `TD` padding (16px each side). */
const CELL_HORIZONTAL_PADDING_PX = 32;
/** frontend-commons DataGrid action column width. */
const ACTION_COLUMN_WIDTH_PX = 50;
/** frontend-commons DataGrid `TBody` vertical scrollbar width. */
const BODY_SCROLLBAR_WIDTH_PX = 8;

const resolveColumnWidthPx = (style) =>
  (style?.width ?? style?.minWidth ?? DEFAULT_COLUMN_STYLE.width) + CELL_HORIZONTAL_PADDING_PX;

/**
 * Table `sx` for the submissions DataGrid. The commons `TBody` is `display: block` with
 * `overflow-x: hidden`, so rows wider than the table are clipped while the header keeps
 * scrolling. Sizing the table to the sum of its fixed column widths keeps body cells visible
 * when the grid is scrolled right.
 * @param {Record<string, { width?: number, minWidth?: number }>} tdStyles - from {@link buildFormSubmissionsTdStyles}
 * @returns {{ width: string, minWidth: number }}
 */
export const buildFormSubmissionsTableSx = (tdStyles) => {
  const columnsWidth = Object.values(tdStyles ?? {}).reduce(
    (total, style) => total + resolveColumnWidthPx(style),
    0,
  );
  const actionWidth = ACTION_COLUMN_WIDTH_PX + CELL_HORIZONTAL_PADDING_PX;
  return {
    width: '100%',
    minWidth: columnsWidth + actionWidth + BODY_SCROLLBAR_WIDTH_PX,
  };
};
